import * as React from "react";

import * as Api from "ApiContracts/control/api/api";
import * as ApiUtils from "ApiUtils";
import { LogoText } from "Logo";
import { Copy, Check, Lock, LoaderCircle, TimerIcon, TriangleAlertIcon } from "lucide-react";

function getLinkId(): string | null {
  const parts = window.location.pathname.split("/");
  if (parts.length >= 3) {
    return parts[2];
  }
  return null;
}

export function LinkPage(): React.ReactElement {
  const [linkUrl, setLinkUrl] = React.useState<string | null>(null);
  const [pingConn, setPingConn] = React.useState<WebSocket | null>(null);
  const [isCopied, setCopied] = React.useState<boolean>(false);
  const [expiry, setExpiry] = React.useState<Date | null>();

  React.useEffect(() => {
    const controller: AbortController = new AbortController();
    const { signal } = controller;

    async function init(): Promise<void> {
      const linkId = getLinkId();
      if (!linkId) {
        console.error(`missing link_id parameter`);
        return;
      }

      if (!linkUrl) {
        const request: Api.GetLink_Request = { linkId };
        const response: Response = await ApiUtils.post("/api/GetLink", request, { signal });
        await ApiUtils.assertStatus(response, 200);
        const { linkUrl } = (await response.json()) as Api.GetLink_Response;

        const expiry = new URL(linkUrl).searchParams.get("expiry");
        if (!expiry) {
          throw new Error(`Couldn't find "expiry" param in URL: ${linkUrl}`);
        }
        const date = new Date(expiry);
        if (isNaN(+date)) {
          throw new Error(`Invalid date: ${expiry}`);
        }

        setExpiry(date);
        setLinkUrl(linkUrl);
        if (date.valueOf() < Date.now()) {
          return;
        }
      } else if (!pingConn) {
        const ws = new WebSocket(`/api/PingLink?link_id=${linkId}`);
        ws.onopen = (): void => {
          console.debug("opened ping conn");
          const id = setInterval((): void => {
            ws.send("ping");
          }, 1000);
          ws.onclose = (): void => {
            clearInterval(id);
            setTimeout((): void => setPingConn(null), 1000);
          };
        };
        setPingConn(ws);
      }
    }

    init().catch((error) => {
      if (signal.aborted) {
        // Do nothing
      } else {
        throw error;
      }
    });

    return (): void => {
      pingConn?.close();
      controller.abort("Cleaning up effect init");
    };
  }, [linkUrl, pingConn]);

  const linkId = getLinkId();

  const copyLink = async (): Promise<void> => {
    await navigator.clipboard.writeText(`https://subt.link/${linkId}`);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (!linkId) {
    document.location = "/";
    return <React.Fragment />;
  }

  if (!linkUrl || !expiry) {
    return (
      <div className="w-screen h-screen flex justify-center items-center">
        <LoaderCircle className="animate-spin text-zinc-500 w-8 h-8" />
      </div>
    );
  }

  const commitHash = (window as any).subtrace.version.commitHash;

  return (
    <div className="w-screen h-screen flex flex-col space-y-2 p-2">
      <div className="grow-0 shrink-0 px-4 py-2 w-full flex justify-between items-center">
        <a href="/" className="w-32 px-2 py-1 -mx-1">
          <LogoText className="text-white" />
        </a>
        <div className="flex items-center h-full space-x-4">
          <button onClick={copyLink} className="flex h-full bg-zinc-900 border border-zinc-800 group rounded">
            <div className="flex h-full text-[10px] select-none items-center px-4 space-x-2 text-zinc-500 bg-zinc-800 uppercase">
              <Lock className="w-[12px] h-[12px]" />
              <span>Share</span>
            </div>
            <div className="group-hover:bg-zinc-800 flex items-center space-x-2 pr-2">
              <div className="text-[10px] cursor-pointer text-zinc-300 font-mono px-4 py-2 select-all">https://subt.link/{linkId}</div>
              {isCopied ? <Check className="w-4 h-4 text-zinc-400" /> : <Copy className="opacity-0 group-hover:opacity-[1.00] w-4 h-4 text-zinc-600" />}
            </div>
          </button>
          <Countdown expiry={expiry} />
        </div>
      </div>
      <div className="grow w-full h-full">
        {expiry.valueOf() > performance.timeOrigin ? (
          <iframe
            sandbox="allow-modals allow-popups allow-scripts allow-same-origin allow-downloads"
            allow="clipboard-write"
            className="rounded h-full w-full"
            src={`/iframer?commit=${commitHash}#url=${encodeURIComponent(linkUrl)}`}
          />
        ) : (
          <div className="w-full h-full flex flex-col items-center justify-center space-y-6 bg-zinc-900 rounded">
            <div className="flex flex-col items-center space-y-4">
              <div className="text-center space-y-2 text-md text-zinc-500 font-medium">
                <p className="max-w-md select-none">This link has expired.</p>
                <p className="max-w-md select-none">
                  Run <span className="font-mono font-normal text-[15px] bg-zinc-700 text-white rounded-sm px-2 py-1 mx-1">subtrace run --</span> again to get a new one.
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function Countdown(props: { expiry: Date }): React.ReactNode {
  const [msLeft, setMsLeft] = React.useState<number>(props.expiry.valueOf() - Date.now());
  React.useEffect(() => {
    const interval = setInterval(() => {
      setMsLeft(props.expiry.valueOf() - Date.now());
    }, 1000);

    return () => {
      clearInterval(interval);
    };
  }, [props.expiry]);

  if (props.expiry.valueOf() <= performance.timeOrigin) {
    return null;
  }

  if (msLeft <= 0) {
    return (
      <span className="text-zinc-700 text-[8px] items-center relative group p-1 pb-0 rounded-sm hover:text-zinc-600 hover:cursor-pointer">
        <span className="flex flex-col items-center space-y-1">
          <TriangleAlertIcon size={18} className="text-yellow-700" />
        </span>
        <div className="z-10 hidden absolute -bottom-4 translate-y-full right-0 group-hover:inline-block">
          <div className="flex flex-col space-y-1 bg-zinc-800 max-w-xs w-max border border-zinc-700 rounded px-2 py-1 text-[10px] text-zinc-300 shadow-sm text-center">
            <span>This link has expired, and will not work if the page is refreshed.</span>
            <span>
              <span>Run</span>
              <span className="text-[9px] font-mono text-zinc-100 bg-zinc-700 rounded-sm px-1 py-0.5 mx-1">subtrace run --</span>
              <span>again to get a new one.</span>
            </span>
          </div>
        </div>
      </span>
    );
  }

  const days = Math.floor(msLeft / 24 / 3600 / 1000);
  const hours = Math.floor((msLeft % (24 * 3600 * 1000)) / (3600 * 1000));
  const minutes = Math.floor((msLeft % (3600 * 1000)) / (60 * 1000));
  const seconds = Math.floor((msLeft % (60 * 1000)) / 1000);

  let remaining = "";
  let remainingShort = "";
  if (days > 0) {
    remaining = `${days}d ${hours.toString().padStart(2, "0")}h`;
    remainingShort = `${days}d`;
  } else if (hours > 0) {
    remaining = `${hours}h ${minutes.toString().padStart(2, "0")}m`;
    remainingShort = `${hours}h`;
  } else if (minutes > 0) {
    remaining = `${minutes.toString().padStart(2, "0")}m ${seconds.toString().padStart(2, "0")}s`;
    remainingShort = `${minutes.toString().padStart(2, "0")}m`;
  } else {
    remaining = `${seconds.toString().padStart(2, "0")}s`;
    remainingShort = `${seconds.toString().padStart(2, "0")}s`;
  }

  return (
    <span className="self-end text-zinc-700 text-[8px] items-center relative group p-1 pb-0 rounded-sm hover:text-zinc-600 hover:cursor-pointer">
      <span className="flex flex-col items-center space-y-1">
        <TimerIcon size={12} />
        <span className="font-mono select-none">{remainingShort}</span>
      </span>
      <div className="z-10 hidden absolute -bottom-2 translate-y-full right-0 group-hover:inline-block">
        <div className="bg-zinc-800 border border-zinc-700 rounded px-2 py-1 text-[10px] text-zinc-300 whitespace-nowrap shadow-sm">
          <span>This link is valid for </span>
          <span className="text-[9px] font-mono text-zinc-100">{remaining}</span>
        </div>
      </div>
    </span>
  );
}
